import React, { useContext, useState } from 'react';
import { Alert, Button, Loader, MultiSelect, Pagination, Select } from '@mantine/core';
import { useQuery } from 'react-query';
import { saveAs } from 'file-saver';
import toast from 'react-hot-toast';
import { AuthContext } from 'context/authContext';
import useLeaveReportAccess from 'hooks/useLeaveReportAccess';
import dateFormatter from 'helper/dateFormatter';
import makeApiRequest from 'services/makeApiRequest';
import apiEndPoints from 'services/apiEndPoints';

const monthOptions = Array.from({ length: 12 }, (_, index) => ({
  value: String(index + 1),
  label: new Date(2026, index, 1).toLocaleString('en', { month: 'long' }),
}));
const statusOptions = [
  { value: 'processed', label: 'Processed requests' },
  { value: 'all', label: 'All requests' },
  { value: 'Approved', label: 'Approved' },
  { value: 'Declined', label: 'Rejected' },
  { value: 'Cancelled', label: 'Cancelled' },
  { value: 'Pending', label: 'Pending' },
];

export default function LeaveReports() {
  const { authState } = useContext(AuthContext);
  const access = useLeaveReportAccess();
  const [filters, setFilters] = useState({
    year: String(new Date().getFullYear()),
    month: String(new Date().getMonth() + 1),
    employees: [],
    leave_type: null,
    status: 'processed',
  });
  const [page, setPage] = useState(1);
  const [downloading, setDownloading] = useState(false);
  const params = {
    ...(filters.year ? { year: filters.year } : {}),
    ...(filters.month ? { month: filters.month } : {}),
    ...(filters.employees.length ? { employees: filters.employees.join(',') } : {}),
    ...(filters.leave_type ? { leave_type: filters.leave_type } : {}),
    status: filters.status,
  };
  const report = useQuery(
    ['leave-report', authState?.user_id, authState?.token, params, page],
    async () => (await makeApiRequest.get(apiEndPoints.LEAVE_REPORTS, {
      params: { ...params, page },
    })).data,
    { enabled: access.data?.can_view === true, retry: false }
  );

  const updateFilter = (key, value) => {
    setPage(1);
    setFilters((previous) => ({
      ...previous,
      [key]: value,
      ...(key === 'year' && !value ? { month: null } : {}),
    }));
  };

  const downloadReport = async () => {
    setDownloading(true);
    try {
      const response = await makeApiRequest.get(apiEndPoints.LEAVE_REPORTS, {
        params: { ...params, export: 'xlsx' },
        responseType: 'blob',
      });
      saveAs(response.data, `leave-report-${filters.year || 'all-years'}${filters.month ? `-${filters.month.padStart(2, '0')}` : ''}.xlsx`);
    } catch (error) {
      toast.error(error?.response?.status === 403
        ? 'Leave reports are available to HR and admins only.'
        : 'Unable to download the leave report. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  if (!access.data?.can_view) return null;

  return (
    <section aria-label='Leave reports' className='mt-4'>
      <div className='rounded-xl bg-white border border-8B8B8B border-opacity-10 shadow-sm p-5'>
        <div className='flex flex-wrap justify-between items-start gap-3'>
          <div>
            <h2 className='text-xl text-gray font-semibold'>Leave reports</h2>
            <p className='text-sm text-gray mt-1'>Filter leave requests and download the full results with an employee summary.</p>
          </div>
          <Button onClick={downloadReport} loading={downloading} disabled={report.isLoading || report.isError || !report.data?.total_count}>
            Download Excel
          </Button>
        </div>
        <div className='grid gap-4 mt-5' style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))' }}>
          <Select
            label='Leave type'
            placeholder='All leave types'
            data={access.data.leave_types}
            value={filters.leave_type}
            onChange={(value) => updateFilter('leave_type', value)}
            searchable clearable nothingFound='No leave types found'
          />
          <Select
            label='Year' placeholder='All years'
            data={access.data.years.map((year) => String(year))}
            value={filters.year} onChange={(value) => updateFilter('year', value)}
            clearable
          />
          <Select
            label='Month' placeholder='All months' data={monthOptions}
            value={filters.month} onChange={(value) => updateFilter('month', value)}
            disabled={!filters.year} clearable
          />
          <MultiSelect
            label='People (active employees)' placeholder='All employees'
            data={access.data.employees} value={filters.employees}
            onChange={(value) => updateFilter('employees', value)}
            searchable clearable nothingFound='No active employees found'
          />
          <Select
            label='Request status' data={statusOptions} value={filters.status}
            onChange={(value) => updateFilter('status', value)}
          />
        </div>
        <div className='flex flex-wrap justify-between items-center gap-2 mt-4'>
          <p className='text-xs text-gray'>Includes requests overlapping the selected period. Duration shows the full request length.</p>
          <Button variant='subtle' size='xs' onClick={() => {
            setFilters({ year: null, month: null, employees: [], leave_type: null, status: 'processed' });
            setPage(1);
          }}>Clear filters</Button>
        </div>
      </div>

      {report.isError ? (
        <Alert color='red' title='Unable to load the report' className='mt-4' role='alert'>
          {report.error?.response?.status === 403
            ? 'Leave reports are available to HR and admins only.'
            : 'Please try loading the report again.'}
          <Button variant='subtle' color='red' size='xs' onClick={() => report.refetch()}>Retry</Button>
        </Alert>
      ) : report.isLoading ? (
        <div className='flex justify-center py-12' role='status' aria-label='Loading leave report'><Loader /></div>
      ) : (
        <>
          <p className='text-sm text-gray my-4' aria-live='polite'>
            {report.data?.total_count || 0} matching request{report.data?.total_count === 1 ? '' : 's'} · Excel includes all matching requests
          </p>
          <div className='rounded-xl bg-white shadow-sm border border-8B8B8B border-opacity-10 overflow-auto max-h-[60vh]'>
            <table className='w-full text-sm text-left'>
              <thead className='bg-FFE8E1 text-222222 sticky top-0'>
                <tr>{['Employee', 'Dept.', 'Leave type', 'Leave period', 'Request days', 'Status', 'Reason', 'Processed by'].map((heading) => (
                  <th key={heading} scope='col' className='px-4 py-4 whitespace-nowrap'>{heading}</th>
                ))}</tr>
              </thead>
              <tbody>
                {report.data?.data?.length ? report.data.data.map((leave) => (
                  <tr key={leave.id} className='border-b border-gray border-opacity-10 text-gray'>
                    <td className='px-4 py-4 min-w-[180px]'><p>{leave.name}</p><p className='text-xs text-A8A8A8'>{leave.employee_id}</p></td>
                    <td className='px-4 py-4'>{leave.department || '—'}</td>
                    <td className='px-4 py-4 text-primary whitespace-nowrap'>{leave.leave_type}</td>
                    <td className='px-4 py-4 whitespace-nowrap'>
                      {leave.from_date ? dateFormatter(leave.from_date, 'DD MMM YYYY') : '—'}<br />
                      to {leave.to_date ? dateFormatter(leave.to_date, 'DD MMM YYYY') : '—'}
                    </td>
                    <td className='px-4 py-4'>{leave.number_of_days ?? '—'}</td>
                    <td className={`px-4 py-4 ${leave.status === 'Approved' ? 'text-success' : ''}`}>{leave.status === 'Declined' ? 'Rejected' : leave.status}</td>
                    <td className='px-4 py-4 min-w-[220px] break-words'>{leave.reason || '—'}</td>
                    <td className='px-4 py-4'>{leave.updated_by || '—'}</td>
                  </tr>
                )) : <tr><td colSpan={8} className='text-center text-gray py-12'>No leave requests match these filters.</td></tr>}
              </tbody>
            </table>
          </div>
          {(report.data?.total_count || 0) > 50 && (
            <Pagination className='my-4' page={page} onChange={setPage} total={Math.ceil(report.data.total_count / report.data.page_size)} />
          )}
        </>
      )}
    </section>
  );
}
